import { useState } from 'react';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { RTKQError } from '../../../../../common/api/api-helpers';
import { setPendingPostLoginRedirect } from '../../../../../common/post-login-redirect';
import { useGetDataExportDownloadUrlMutation } from './dataExportApi';

const REAUTH_REQUIRED_CODE = 'reauth_required';

/**
 * Hands a signed download URL to the OS. The payload is a ZIP behind a short-lived signed URL, so we
 * never fetch or parse it — we just open it and let the platform's download/share handling take over.
 */
async function openDownloadUrl(url: string) {
  if (Capacitor.isNativePlatform()) {
    await Browser.open({ url });
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

/**
 * Resolves a data export's signed download URL and hands it to the OS, mapping the contract's download
 * error responses to UX:
 *  - 401 `reauth_required`: session older than 5 min — surface a confirmation dialog before routing
 *    through `/logout` for a fresh federated login, so the user isn't logged out without warning.
 *    We persist the current page so login can send them straight back here.
 *  - 410: the file has expired — surface a message and refresh so the user can request a new export.
 *  - 404: the job no longer exists — refresh the list.
 *
 * @param onListShouldRefresh Re-fetches the export list (called on 404/410 to reconcile stale rows).
 */
export function useDownloadDataExport(onListShouldRefresh: () => void) {
  const { t } = useTranslation();
  const toast = useToast({ isClosable: true });
  const navigate = useNavigate();
  const location = useLocation();
  const [getDownloadUrl] = useGetDataExportDownloadUrlMutation();
  const [downloadingReferenceId, setDownloadingReferenceId] = useState<string | null>(null);
  const [isReauthDialogOpen, setIsReauthDialogOpen] = useState(false);

  async function downloadExport(referenceId: string) {
    setDownloadingReferenceId(referenceId);
    try {
      const { download_url: downloadUrl } = await getDownloadUrl(referenceId).unwrap();
      await openDownloadUrl(downloadUrl);
    } catch (err) {
      handleDownloadError(err as RTKQError);
    } finally {
      setDownloadingReferenceId(null);
    }
  }

  function handleDownloadError(error: RTKQError) {
    const code = error.originalError?.response?.data?.code;

    if (error.status === 401 && code === REAUTH_REQUIRED_CODE) {
      setIsReauthDialogOpen(true);
      return;
    }

    if (error.status === 410) {
      toast({ title: t('Data Archive.file expired'), status: 'warning' });
      onListShouldRefresh();
      return;
    }

    if (error.status === 404) {
      onListShouldRefresh();
      return;
    }

    toast({
      title: t('Data Archive.error downloading'),
      description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
      status: 'error',
    });
  }

  function confirmReauth() {
    setIsReauthDialogOpen(false);
    setPendingPostLoginRedirect(`${location.pathname}${location.search}`);
    navigate('/logout');
  }

  function cancelReauth() {
    setIsReauthDialogOpen(false);
  }

  return { downloadExport, downloadingReferenceId, isReauthDialogOpen, confirmReauth, cancelReauth };
}
